/**
 * The two decisions the referral screen makes, kept out of JSX so they can be
 * asserted: what a typed code becomes, and what a failure says to the friend.
 *
 * The second matters more than it looks. A referral code arrives by WhatsApp,
 * gets pasted with a trailing space, or is read aloud and typed in lowercase —
 * and the person typing it has no account, no subscription and no reason to try
 * twice. Collapsing every failure into "משהו השתבש" turns four fixable problems
 * (typo, own code, already a customer, too soon) into one dead end.
 */

/** What the app shows for a redemption that did not go through. */
export interface RedeemFailure {
  /** Hebrew, RTL, addressed to the friend rather than to us. */
  message: string;
  /**
   * Whether trying the same code again could plausibly work. Drives whether the
   * form stays usable or the friend is pointed at the paywall instead.
   */
  retryable: boolean;
  /** True when the cause is ours, not theirs — worth logging loudly. */
  ourFault: boolean;
}

/**
 * Codes are stored and compared upper-case, and the hub upper-cases anyway. Doing
 * it here too means the field shows the friend the same thing the server will see,
 * rather than silently transforming their input on submit.
 *
 * All whitespace is stripped, not just the ends: a code read out over the phone
 * is often typed "ABC 12345".
 */
export function normalizeReferralCode(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

/** Whether the code is worth sending. Deliberately permissive — the hub is the judge. */
export function isSubmittableCode(raw: string): boolean {
  return normalizeReferralCode(raw).length > 0;
}

const OUR_FAULT: RedeemFailure = {
  // Never names the real cause. A missing bridge secret or a 400 from our own
  // payload is not something the friend can act on, and telling them the code is
  // wrong when it is not would send them back to the member for a new one.
  message: "משהו השתבש אצלנו. נסו שוב בעוד רגע, ואם זה חוזר — כתבו לנו.",
  retryable: true,
  ourFault: true,
};

const FAILURES: Record<string, RedeemFailure> = {
  unknown_code: {
    message: "הקוד לא נמצא. בדקו שהקלדתם נכון.",
    retryable: true,
    ourFault: false,
  },
  program_closed: {
    message: "התוכנית סגורה כרגע.",
    retryable: false,
    ourFault: false,
  },
  self_referral: {
    message: "אי אפשר להשתמש בקוד של עצמכם.",
    retryable: true,
    ourFault: false,
  },
  already_known: {
    message: "ההטבה מיועדת למצטרפים חדשים בלבד.",
    retryable: false,
    ourFault: false,
  },
  internal_blocked: {
    // Documented as unreachable for real users; worded harmlessly in case it is not.
    message: "לא ניתן לממש את הקוד הזה בחשבון הזה.",
    retryable: false,
    ourFault: false,
  },
  code_at_capacity: {
    message: "הקוד הזה הגיע למכסה. נסו מאוחר יותר.",
    retryable: true,
    ourFault: false,
  },
  rate_limited: {
    message: "נסו שוב בעוד רגע.",
    retryable: true,
    ourFault: false,
  },

  // Ours, not theirs.
  unauthorized: OUR_FAULT,
  not_configured: OUR_FAULT,
  hub_error: OUR_FAULT,
  hub_unreachable: {
    message: "אין כרגע חיבור לשרת. בדקו את החיבור לאינטרנט ונסו שוב.",
    retryable: true,
    ourFault: false,
  },
  invalid_code: {
    message: "הקוד לא נמצא. בדקו שהקלדתם נכון.",
    retryable: true,
    ourFault: false,
  },

  // No `invalid_phone` entry, deliberately. The edge function sends only the
  // verified AUTH phone and omits any the hub would reject (redeem-referral/
  // phone.ts), so that code can only mean our payload broke the hub's contract —
  // it falls through to the generic our-fault message below.

  // Raised by our own edge function when the account has no AUTH email. The name
  // predates the auth-only rule and is kept because this map matches on it. The
  // function never reads profiles.email, so adding one in Settings fixes nothing
  // — the only way out is support.
  missing_profile_email: {
    message: "לא מצאנו כתובת מייל בחשבון שלכם, ולכן אי אפשר לממש את הקוד. פנו לתמיכה ונעזור.",
    retryable: false,
    ourFault: false,
  },
};

/**
 * @param error the `error` code from redeem-referral, or null when the call
 *              itself never produced one (offline, or a body we could not read)
 */
export function describeRedeemFailure(error: string | null | undefined): RedeemFailure {
  if (!error) return OUR_FAULT;
  return FAILURES[error] ?? OUR_FAULT;
}

/**
 * Whether the referral program is switched on, from the app_config read.
 *
 * Unlike `deriveFlagState` (which every other kill-switch uses) this is NOT
 * native-only. An invite link opens in a browser whenever the friend has no app
 * yet — which is most of them — and that page has to work.
 *
 * Redeeming on web is safe for attribution: the referral is keyed on the Supabase
 * user id, not the channel, so a purchase made later in the app still links. The
 * one hole is a friend who redeems on web and then buys through the ordinary web
 * checkout — that produces no RevenueCat event and no `commitReferralSignup`, so
 * the referrer never earns. Which is exactly why the invite page offers the app
 * and never a web-checkout link.
 *
 * Fails CLOSED: a missing row, a read error, or an unsettled read all read false.
 */
export function deriveReferralFlag(input: { data: boolean | undefined; isError: boolean }): {
  enabled: boolean;
  loading: boolean;
} {
  return {
    enabled: input.data === true,
    loading: input.data === undefined && !input.isError,
  };
}

/**
 * The offer, in the friend's words.
 *
 * `trialDays` comes from the STORE at runtime (see `resolvePackageTrialOffer`),
 * never from a constant here. Both stores are configured for 14 days today, but
 * only on the monthly plan and only for new subscribers — and Apple's offer is
 * per-territory. Hardcoding the number would put a promise in the app that the
 * store can silently stop honouring, which is the one thing `trialOffer.ts`
 * exists to prevent.
 */
export type TrialKnowledge =
  /** The store confirmed a trial for THIS user. `days` is null when it stated no length. */
  | { kind: "trial"; days: number | null }
  /** The store was asked and said no — an Apple churner, or a territory without the offer. */
  | { kind: "no_trial" }
  /** Not asked (web, where there is no store to ask) or the lookup failed. */
  | { kind: "unknown" };

export function describeReferralOffer(knowledge: TrialKnowledge): string {
  switch (knowledge.kind) {
    case "trial":
      return knowledge.days
        ? `לאחר מימוש הקוד תוכלו להתחיל ${knowledge.days} ימי מנוי חינם. בתום התקופה המנוי מתחדש בתשלום, וניתן לבטל בכל רגע.`
        : "לאחר מימוש הקוד תוכלו להתחיל תקופת מנוי חינם. בתום התקופה המנוי מתחדש בתשלום, וניתן לבטל בכל רגע.";
    case "no_trial":
      // The store already refused this person a trial. Saying "free" here is the
      // false-price problem `trialOffer.ts` exists to prevent — Apple 2.3.1 and
      // Google's Deceptive Behavior policy both treat it as grounds for removal.
      // The referral is still worth redeeming: it is what credits the member who
      // invited them.
      return "מימוש הקוד ישמור לכם את ההטבה. בשלב הבא תוכלו לבחור מסלול מנוי ולהתחיל.";
    case "unknown":
      // Web, mostly — there is no store here to ask, and eligibility is decided
      // later, on the device. Conditional rather than promissory: it must read
      // the same to someone who turns out to be ineligible.
      return "לאחר מימוש הקוד תבחרו מסלול מנוי. אם אתם זכאים לתקופת ניסיון, היא תתחיל בחנות.";
  }
}

/**
 * What a WEB redeemer is told once the referral is booked.
 *
 * Lives here, beside `describeReferralOffer`, because it is bound by the same
 * rule and previously broke it: the panel used to promise "והתחילו את תקופת
 * הניסיון מתוכה" — a trial — to the one audience whose eligibility we can least
 * establish. A browser has no store SDK, so there is no `TrialKnowledge` to read;
 * and even on native the trial is MONTHLY-only, so anyone picking annual or
 * six-month gets none, as does anyone who already spent theirs on this Apple ID.
 *
 * So it promises the step, not the offer. Whatever the store is actually willing
 * to give is stated by the store, on the paywall, where it is true.
 */
export const WEB_BOOKED_BODY =
  "הורידו את האפליקציה והתחברו עם אותו החשבון. את ההצטרפות משלימים מתוך האפליקציה.";
